import React from 'react';
import { clsx } from 'clsx';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import Button from './Button';

export interface Column<T = any> {
  key: string;
  header: string | React.ReactNode;
  accessor?: keyof T | ((item: T) => React.ReactNode);
  render?: (item: T, value: any) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  className?: string;
}

export interface TableProps<T = any> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  emptyState?: React.ReactNode;
  variant?: 'default' | 'striped' | 'bordered';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onRowClick?: (item: T) => void;
  rowClassName?: (item: T) => string;
}

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  showInfo?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

// Table Loading Skeleton
const TableSkeleton: React.FC<{ columns: number; rows?: number }> = ({ columns, rows = 5 }) => {
  return (
    <div className="animate-pulse">
      <div className="table w-full">
        <div className="table-header">
          <div className="table-row">
            {Array.from({ length: columns }).map((_, i) => (
              <div key={i} className="table-head">
                <div className="h-4 bg-muted rounded w-3/4"></div>
              </div>
            ))}
          </div>
        </div>
        <div className="table-body">
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <div key={rowIndex} className="table-row">
              {Array.from({ length: columns }).map((_, colIndex) => (
                <div key={colIndex} className="table-cell">
                  <div className="h-4 bg-muted rounded w-full"></div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Main Table Component
const Table = <T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  emptyState,
  variant = 'default',
  size = 'md',
  className,
  onRowClick,
  rowClassName,
}: TableProps<T>) => {
  const getValue = (item: T, column: Column<T>) => {
    if (typeof column.accessor === 'function') {
      return column.accessor(item);
    }
    if (typeof column.accessor === 'string') {
      return item[column.accessor];
    }
    return item[column.key];
  };

  const sizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  const variantClasses = {
    default: 'table',
    striped: 'table table-striped',
    bordered: 'table table-bordered',
  };

  if (loading) {
    return (
      <div className={clsx('bg-card rounded-lg border border-border overflow-hidden', className)}>
        <TableSkeleton columns={columns.length} />
      </div>
    );
  }

  if (data.length === 0 && emptyState) {
    return (
      <div className={clsx('bg-card rounded-lg border border-border', className)}>
        <div className="p-12 text-center">
          {emptyState}
        </div>
      </div>
    );
  }

  return (
    <div className={clsx('bg-card rounded-lg border border-border overflow-hidden', className)}>
      <div className="overflow-x-auto">
        <table className={clsx(variantClasses[variant], sizeClasses[size])}>
          <thead className="table-header">
            <tr className="table-row">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={clsx(
                    'table-head',
                    column.align === 'center' && 'text-center',
                    column.align === 'right' && 'text-right',
                    column.className
                  )}
                  style={{ width: column.width }}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="table-body">
            {data.map((item, index) => (
              <tr
                key={item.id || index}
                className={clsx(
                  'table-row',
                  onRowClick && 'cursor-pointer',
                  rowClassName?.(item)
                )}
                onClick={() => onRowClick?.(item)}
              >
                {columns.map((column) => {
                  const value = getValue(item, column);
                  return (
                    <td
                      key={column.key}
                      className={clsx(
                        'table-cell',
                        column.align === 'center' && 'text-center',
                        column.align === 'right' && 'text-right',
                        column.className
                      )}
                    >
                      {column.render ? column.render(item, value) : value}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Pagination Component
const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  showInfo = true,
  size = 'md',
  className,
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  const hasPrev = currentPage > 1;
  const hasNext = currentPage < totalPages;

  const sizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  const buttonSizeClasses = {
    sm: 'btn-sm',
    md: '',
    lg: 'btn-lg',
  };

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className={clsx(
      'bg-card px-6 py-4 flex items-center justify-between border-t border-border',
      className
    )}>
      {/* Mobile pagination */}
      <div className="flex-1 flex justify-between sm:hidden">
        <Button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!hasPrev}
          variant="outlined"
          size={size}
        >
          Previous
        </Button>
        <Button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!hasNext}
          variant="outlined"
          size={size}
        >
          Next
        </Button>
      </div>

      {/* Desktop pagination */}
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        {showInfo && (
          <div>
            <p className={clsx('text-muted-foreground', sizeClasses[size])}>
              Showing{' '}
              <span className="font-medium text-foreground">{startItem}</span>
              {' '}to{' '}
              <span className="font-medium text-foreground">{endItem}</span>
              {' '}of{' '}
              <span className="font-medium text-foreground">{totalItems}</span>
              {' '}results
            </p>
          </div>
        )}
        <div>
          <nav className="relative z-0 inline-flex rounded-lg shadow-sm -space-x-px" aria-label="Pagination">
            <Button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={!hasPrev}
              variant="outlined"
              size={size}
              className="rounded-l-lg rounded-r-none"
            >
              <ChevronLeftIcon className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <Button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={!hasNext}
              variant="outlined"
              size={size}
              className="rounded-r-lg rounded-l-none"
            >
              Next
              <ChevronRightIcon className="h-4 w-4 ml-1" />
            </Button>
          </nav>
        </div>
      </div>
    </div>
  );
};

// Export components
export { Table, Pagination, TableSkeleton };
export default Table;